/* eslint-disable no-restricted-syntax */
/**
 * Downloads a Blob object as a file by creating a temporary anchor element
 * and triggering a download. Cleans up the object URL after download.
 *
 * @param blob - The Blob object to download
 * @param filename - The name of the file to save
 */
export function downloadFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Extracts the filename from a Content-Disposition header value.
 * Supports both filename and filename* (RFC 5987) formats.
 *
 * @param contentDisposition - The Content-Disposition header value
 * @returns The extracted filename, or null if not found
 *
 * @example
 * extractFilename('attachment; filename="offer-123.csv"') // returns 'offer-123.csv'
 * extractFilename('attachment; filename*=UTF-8\'\'offer-123.csv') // returns 'offer-123.csv'
 */
export function extractFilename(
  contentDisposition: string | null
): string | null {
  if (!contentDisposition) {
    return null;
  }

  // Try RFC 5987 format (filename*)
  const filenameStarMatch = contentDisposition.match(
    /filename\*=(?:UTF-8''|utf-8'')(.+)/i
  );
  if (filenameStarMatch?.[1]) {
    return decodeURIComponent(filenameStarMatch[1]);
  }

  // Try standard format (filename="...")
  const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  if (filenameMatch?.[1]) {
    return filenameMatch[1];
  }

  return null;
}
